import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  FiUsers, 
  FiBookOpen, 
  FiDollarSign,
  FiPlus,
  FiEdit,
  FiUserCheck,
  FiFileText
} from 'react-icons/fi';
import axios from 'axios';
import toast from 'react-hot-toast';
import dashboardEvents from '../utils/dashboardEvents';
import PurchaseNotification from '../components/ui/PurchaseNotification';

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalCourses: 0,
    totalEnrollments: 0,
    totalRevenue: 0
  });
  const [recentCourses, setRecentCourses] = useState([]);
  const [recentUsers, setRecentUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchaseNotification, setPurchaseNotification] = useState({
    isVisible: false,
    courses: []
  });

  useEffect(() => {
    fetchDashboardData();
    
    // Listen for stats update events
    const handleStatsUpdated = (data) => {
      console.log('Stats updated, refreshing admin dashboard:', data);
      fetchDashboardData();
      
      // Show purchase notification for admin
      if (data.type === 'purchase') {
        setPurchaseNotification({
          isVisible: true,
          courses: data.data.courses
        });
      }
    };
    
    dashboardEvents.on('stats-updated', handleStatsUpdated);
    
    // Cleanup listeners on unmount
    return () => {
      dashboardEvents.off('stats-updated', handleStatsUpdated);
    };
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch stats
      const statsResponse = await axios.get('/api/admin/dashboard');
      setStats(statsResponse.data);
      
      // Fetch recent courses
      const coursesResponse = await axios.get('/api/admin/courses');
      setRecentCourses(coursesResponse.data.slice(0, 5)); // Get only first 5 courses
      
      // Fetch recent users
      const usersResponse = await axios.get('/api/admin/users?limit=5');
      setRecentUsers(usersResponse.data.users || []);
      
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('대시보드 데이터를 불러오는데 실패했습니다');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="loading-spinner mx-auto"></div>
          <p className="mt-4 text-gray-600">로딩 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Purchase Notification */}
      <PurchaseNotification
        isVisible={purchaseNotification.isVisible}
        courses={purchaseNotification.courses}
        onClose={() => setPurchaseNotification({ isVisible: false, courses: [] })}
      />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">관리자 대시보드</h1>
          <p className="mt-2 text-gray-600">시스템 전체 현황을 확인하고 관리하세요</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-blue-100">
                <FiUsers className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">총 사용자</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalUsers.toLocaleString()}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-green-100">
                <FiBookOpen className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">총 강의</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalCourses.toLocaleString()}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-purple-100">
                <FiUserCheck className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">총 수강생</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalEnrollments.toLocaleString()}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-yellow-100">
                <FiDollarSign className="w-6 h-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">총 매출</p>
                <p className="text-2xl font-bold text-gray-900">₩{stats.totalRevenue.toLocaleString()}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">빠른 작업</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Link
              to="/admin/courses/create"
              className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <FiPlus className="w-5 h-5 text-green-600 mr-3" />
              <span className="font-medium">새 강의 추가</span>
            </Link>
            
            <Link
              to="/admin/courses"
              className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <FiBookOpen className="w-5 h-5 text-blue-600 mr-3" />
              <span className="font-medium">강의 관리</span>
            </Link>
            
            <Link
              to="/admin/users"
              className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <FiUsers className="w-5 h-5 text-purple-600 mr-3" />
              <span className="font-medium">사용자 관리</span>
            </Link>
            
            <Link
              to="/admin/enrollments"
              className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <FiUserCheck className="w-5 h-5 text-orange-600 mr-3" />
              <span className="font-medium">수강 관리</span>
            </Link>
            
            <Link
              to="/admin/posts"
              className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <FiFileText className="w-5 h-5 text-indigo-600 mr-3" />
              <span className="font-medium">게시물 관리</span>
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Recent Courses */}
          <div className="bg-white rounded-lg shadow-sm">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">최근 강의</h3>
                <Link to="/admin/courses" className="text-sm text-primary-600 hover:text-primary-700">
                  모두 보기
                </Link>
              </div>
            </div>
            <div className="p-6">
              {recentCourses.length === 0 ? (
                <p className="text-gray-500 text-center py-4">등록된 강의가 없습니다</p>
              ) : (
                <div className="space-y-4">
                  {recentCourses.map((course) => (
                    <div key={course._id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                      <div className="flex items-center">
                        <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center mr-4">
                          <FiBookOpen className="w-6 h-6 text-gray-500" />
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900">{course.title}</h4>
                          <p className="text-sm text-gray-600">{course.enrolledStudents}명 수강</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Link
                          to={`/admin/courses/${course._id}/edit`}
                          className="p-2 text-gray-400 hover:text-blue-600"
                        >
                          <FiEdit className="w-4 h-4" />
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Recent Users */}
          <div className="bg-white rounded-lg shadow-sm">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">최근 가입자</h3>
                <Link to="/admin/users" className="text-sm text-primary-600 hover:text-primary-700">
                  모두 보기
                </Link>
              </div>
            </div>
            <div className="p-6">
              {recentUsers.length === 0 ? (
                <p className="text-gray-500 text-center py-4">가입한 사용자가 없습니다</p>
              ) : (
                <div className="space-y-4">
                  {recentUsers.map((user) => (
                    <div key={user._id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                      <div className="flex items-center">
                        <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center mr-4">
                          <FiUsers className="w-6 h-6 text-gray-500" />
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900">{user.name}</h4>
                          <p className="text-sm text-gray-600">{user.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-500">{user.role}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard; 